<p-toast></p-toast>

<div class="linked-device-detail-page">
    @if (loading()) {
    <div class="flex flex-col gap-4">
        <p-skeleton height="2rem" width="20rem"></p-skeleton>
        <p-skeleton height="16rem"></p-skeleton>
    </div>
    } @else if (error()) {
    <p-message severity="error">{{ error()! }}</p-message>
    } @else if (device(); as d) {
    <app-breadcrumbs [home]="breadcrumbHome" [model]="breadcrumbItems()" />

    <!-- Header -->
    <header class="ld-header">
        <span class="ld-header__icon" aria-hidden="true"><i [class]="deviceIcon()"></i></span>
        <div class="ld-header__main">
            <div class="ld-header__title-row">
                <h1 class="ld-header__title">{{ displayName(d) }}</h1>
                <app-status-badge [status]="statusDescriptor()" size="sm" />
            </div>
            <p class="ld-header__subtitle">
                @if (makeModel()) {
                <span>{{ makeModel() }}</span><span aria-hidden="true">·</span>
                }
                <span>{{ d.type }}</span>
            </p>
            <ul class="ld-header__meta">
                <li>
                    <i class="pi pi-server" aria-hidden="true"></i><span class="sr-only">{{ t()('linked.controlSystem') }}: </span>
                    @if (d.parentDeviceId) {
                    <a [routerLink]="['/device', d.parentDeviceId]">{{ parentDeviceName() }}</a>
                    } @else {
                    <span>{{ parentDeviceName() }}</span>
                    }
                </li>
                <li>
                    <i class="pi pi-map-marker" aria-hidden="true"></i><span class="sr-only">{{ t()('linked.room') }}: </span>
                    @if (d.locationNodeId && d.appNamespaceId) {
                    <a [routerLink]="['/dashboard', d.appNamespaceId, d.locationNodeId]">{{ locationLabel() }}</a>
                    } @else {
                    <span class="ld-muted">{{ t()('linked.noRoom') }}</span>
                    }
                </li>
                <li [pTooltip]="formatExact(d.lastSeenUtc)" tooltipPosition="bottom"><i class="pi pi-clock" aria-hidden="true"></i><span class="sr-only">{{ t()('linked.lastSeen') }}: </span><span>{{ formatLastSeen() }}</span></li>
            </ul>
        </div>
        <div class="ld-header__actions">
            <p-button [label]="t()('linked.move')" icon="pi pi-arrow-right" severity="secondary" [outlined]="true" (onClick)="openMoveDialog()" />
            @if (d.missing) {
            <p-button [label]="t()('linked.remove')" icon="pi pi-trash" severity="danger" [outlined]="true" (onClick)="openRemoveDialog()" />
            }
        </div>
    </header>

    @if (tunnelConnected() === false) {
    <p-message severity="warn" [closable]="false" styleClass="ld-tunnel">
        <div class="flex items-start gap-2">
            <i class="pi pi-cloud mt-1" aria-hidden="true"></i>
            <div>
                <strong>{{ t()('linked.tunnelTitle') }}</strong>
                <div class="text-sm mt-1">{{ t()('linked.tunnelDetail').replace('{system}', parentDeviceName()) }}</div>
            </div>
        </div>
    </p-message>
    }

    <!-- Tabs -->
    <p-tabs class="ld-tabs" [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
            <p-tab value="overview"><i class="pi pi-info-circle" aria-hidden="true"></i><span>{{ t()('linked.tab.overview') }}</span></p-tab>
            @if (hasOutlets()) {
            <p-tab value="outlets"><i class="fa fa-plug-circle-bolt" aria-hidden="true"></i><span>{{ t()('linked.tab.outlets') }}</span></p-tab>
            } @if (hasCommands()) {
            <p-tab value="commands"><i class="pi pi-code" aria-hidden="true"></i><span>{{ t()('linked.tab.commands') }}</span><span class="ld-tab-count">{{ d.commands.length }}</span></p-tab>
            } @if (hasDiagnostics()) {
            <p-tab value="diagnostics"><i class="pi pi-wrench" aria-hidden="true"></i><span>{{ t()('linked.tab.diagnostics') }}</span></p-tab>
            }
        </p-tablist>

        <p-tabpanels>
            <!-- ==================== OVERVIEW ==================== -->
            <p-tabpanel value="overview">
                <div class="ld-overview">
                    <div class="ld-column">
                        @if (hasControlCapabilities()) {
                        <app-section-card class="ld-control" icon="pi pi-sliders-h" [title]="t()('linked.control.title')" [subtitle]="t()('linked.control.subtitle')">
                            @if (controlBlockedReason(); as reason) {
                            <p class="ld-note" role="status"><i class="pi pi-lock" aria-hidden="true"></i><span>{{ reason }}</span></p>
                            }
                            <div class="ld-control-rows">
                                @if (d.capabilities?.hasPower) {
                                <div class="ld-control-row">
                                    <div class="ld-control-row__label">
                                        <span>{{ t()('linked.control.power') }}</span>
                                        <p-tag
                                            [value]="powerStatusLabel(d.state?.powerStatus)"
                                            [severity]="powerStatusSeverity(d.state?.powerStatus)"
                                            [icon]="d.state?.powerStatus === 'PowerWarming' || d.state?.powerStatus === 'PowerCooling' ? 'pi pi-spin pi-spinner' : ''"
                                        />
                                    </div>
                                    <p-button
                                        [label]="t()(d.state?.powerStatus === 'PowerOn' ? 'linked.control.powerOff' : 'linked.control.powerOn')"
                                        icon="pi pi-power-off"
                                        size="small"
                                        [outlined]="true"
                                        [severity]="d.state?.powerStatus === 'PowerOn' ? 'danger' : 'success'"
                                        [loading]="controlExecuting() === 'setPower'"
                                        [disabled]="controlExecuting() !== null || !!controlBlockedReason()"
                                        (onClick)="togglePower()"
                                    />
                                </div>
                                } @if (d.capabilities?.hasStandby) {
                                <div class="ld-control-row">
                                    <div class="ld-control-row__label">
                                        <span>{{ t()('linked.control.standby') }}</span>
                                        <p-tag [value]="t()(d.state?.inStandby ? 'linked.control.asleep' : 'linked.control.awake')" [severity]="d.state?.inStandby ? 'warn' : 'success'" />
                                    </div>
                                    <p-button
                                        [label]="t()(d.state?.inStandby ? 'linked.control.wake' : 'linked.control.sleep')"
                                        [icon]="d.state?.inStandby ? 'pi pi-sun' : 'pi pi-moon'"
                                        size="small"
                                        severity="secondary"
                                        [outlined]="true"
                                        [loading]="controlExecuting() === 'setStandby'"
                                        [disabled]="controlExecuting() !== null || !!controlBlockedReason()"
                                        (onClick)="toggleStandby()"
                                    />
                                </div>
                                } @if (d.capabilities?.hasInputControl) {
                                <div class="ld-control-row ld-control-row--stacked">
                                    <div class="ld-control-row__label">
                                        <span>{{ t()('linked.control.input') }}</span>
                                        @if (d.state?.currentInput) {
                                        <p-tag [value]="d.state.currentInput" severity="info" />
                                        }
                                    </div>
                                    <div class="ld-inputs" role="group" [attr.aria-label]="t()('linked.control.input')">
                                        @for (input of d.state?.availableInputs ?? []; track input) {
                                        <p-button
                                            [label]="input"
                                            size="small"
                                            [severity]="d.state?.currentInput === input ? 'primary' : 'secondary'"
                                            [outlined]="d.state?.currentInput !== input"
                                            [attr.aria-pressed]="d.state?.currentInput === input"
                                            [loading]="controlExecuting() === 'setInput'"
                                            [disabled]="controlExecuting() !== null || !!controlBlockedReason()"
                                            (onClick)="selectInput(input)"
                                        />
                                        } @empty {
                                        <span class="ld-muted">{{ t()('linked.control.noInputs') }}</span>
                                        }
                                    </div>
                                </div>
                                }
                            </div>
                        </app-section-card>
                        } @if (panelConnections(); as pc) {
                        <app-section-card class="ld-connections" icon="pi pi-link" [title]="t()('linked.connections.title')" [subtitle]="t()('linked.connections.subtitle')">
                            <div class="ld-control-rows">
                                <div class="ld-control-row">
                                    <div class="ld-control-row__label">
                                        <span>{{ t()('linked.connections.ipId').replace('{id}', pc.ipId) }}</span>
                                        <p-tag [value]="t()(pc.ipIdOnline ? 'linked.connections.online' : 'linked.connections.offline')" [severity]="pc.ipIdOnline ? 'success' : 'danger'" />
                                    </div>
                                    @if (pc.ipAddresses.length) {
                                    <span class="ld-mono ld-muted">{{ t()('linked.connections.from').replace('{ips}', pc.ipAddresses.join(', ')) }}</span>
                                    }
                                </div>
                                <div class="ld-control-row">
                                    <div class="ld-control-row__label">
                                        <span>{{ t()('linked.connections.ui') }}</span>
                                        @if (!pc.expectsUi) {
                                        <p-tag [value]="t()('linked.connections.notUsed')" severity="secondary" />
                                        } @else if (pc.uiConnectionCount > 1) {
                                        <p-tag [value]="t()('linked.connections.connectedCount').replace('{n}', '' + pc.uiConnectionCount)" severity="warn" />
                                        } @else if (pc.uiConnectionCount === 1) {
                                        <p-tag [value]="t()('linked.connections.connected')" severity="success" />
                                        } @else {
                                        <p-tag [value]="t()('linked.connections.notConnected')" [severity]="pc.ipIdOnline ? 'warn' : 'secondary'" />
                                        }
                                    </div>
                                    @if (pc.uiRemoteAddresses.length) {
                                    <span class="ld-mono ld-muted">{{ t()('linked.connections.from').replace('{ips}', pc.uiRemoteAddresses.join(', ')) }}</span>
                                    }
                                </div>
                            </div>
                            @if (pc.uiAddress || pc.loadedProject) {
                            <dl class="app-facts">
                                @if (pc.uiAddress) {
                                <div class="app-fact ld-fact--wide">
                                    <dt>{{ t()('linked.connections.address') }}</dt>
                                    <dd class="ld-mono ld-unique-id">{{ pc.uiAddress }}</dd>
                                </div>
                                } @if (pc.loadedProject) {
                                <div class="app-fact ld-fact--wide">
                                    <dt>{{ t()('linked.connections.project') }}</dt>
                                    <dd>{{ pc.loadedProject }}</dd>
                                </div>
                                }
                            </dl>
                            }
                        </app-section-card>
                        }

                        <app-section-card
                            class="ld-health"
                            icon="pi pi-heart"
                            [title]="t()('linked.health.title')"
                            [subtitle]="t()(isPanel() ? 'linked.health.panelSubtitle' : 'linked.health.subtitle')"
                            [tone]="healthMessages().length ? 'warning' : 'primary'"
                            [count]="healthMessages().length || null"
                        >
                            @if (healthMessages().length) {
                            <app-messages-panel [messages]="healthMessages()" [subdued]="true" />
                            } @else if (tunnelConnected() === false) {
                            <app-empty-state icon="pi pi-cloud" [compact]="true" [title]="t()('linked.health.needsTunnel')" [description]="t()('linked.health.needsTunnelDetail')" />
                            } @else {
                            <app-empty-state icon="pi pi-check-circle" tone="success" [compact]="true" [title]="t()('linked.health.ok')" [description]="t()(isPanel() ? 'linked.health.panelOkDetail' : 'linked.health.okDetail')" />
                            }
                        </app-section-card>
                    </div>

                    <div class="ld-column">
                        <app-section-card class="ld-details" icon="pi pi-id-card" [title]="t()('linked.details.title')">
                            <dl class="app-facts">
                                @if (d.manufacturerName) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.manufacturer') }}</dt>
                                    <dd>{{ d.manufacturerName }}</dd>
                                </div>
                                } @if (d.modelName) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.model') }}</dt>
                                    <dd>{{ d.modelName }}</dd>
                                </div>
                                }
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.type') }}</dt>
                                    <dd>{{ d.type }}</dd>
                                </div>
                                @if (d.versionInfo) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.firmware') }}</dt>
                                    <dd>{{ d.versionInfo }}</dd>
                                </div>
                                } @if (d.serialNumber) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.serial') }}</dt>
                                    <dd class="ld-mono">{{ d.serialNumber }}</dd>
                                </div>
                                } @if (d.connectionInfo) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.connection') }}</dt>
                                    <dd class="ld-mono">{{ d.connectionInfo }}</dd>
                                </div>
                                } @if (d.identity) {
                                <div class="app-fact">
                                    <dt>{{ t()('linked.details.identity') }}</dt>
                                    <dd>{{ d.identity }}</dd>
                                </div>
                                }
                                <div class="app-fact ld-fact--wide">
                                    <dt>{{ t()('linked.details.uniqueId') }}</dt>
                                    <dd>
                                        <span class="ld-mono ld-unique-id">{{ d.uniqueId }}</span>
                                        <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" severity="secondary" [ariaLabel]="t()('linked.details.copy')" [pTooltip]="t()('linked.details.copy')" (onClick)="copyUniqueId()" />
                                    </dd>
                                </div>
                            </dl>
                        </app-section-card>

                        <app-section-card class="ld-activity" icon="pi pi-map" [title]="t()('linked.activity.title')">
                            <dl class="app-facts">
                                <div class="app-fact">
                                    <dt>{{ t()('linked.controlSystem') }}</dt>
                                    <dd>
                                        @if (d.parentDeviceId) {
                                        <a [routerLink]="['/device', d.parentDeviceId]">{{ parentDeviceName() }}</a>
                                        } @else { {{ parentDeviceName() }} }
                                    </dd>
                                </div>
                                <div class="app-fact">
                                    <dt>{{ t()('linked.room') }}</dt>
                                    <dd>
                                        @if (d.locationNodeId && d.appNamespaceId) {
                                        <a [routerLink]="['/dashboard', d.appNamespaceId, d.locationNodeId]">{{ locationLabel() }}</a>
                                        } @else {
                                        <span class="ld-muted">{{ t()('linked.noRoom') }}</span>
                                        }
                                    </dd>
                                </div>
                                <div class="app-fact">
                                    <dt>{{ t()('linked.lastSeen') }}</dt>
                                    <dd [title]="formatExact(d.lastSeenUtc)">{{ formatLastSeen() }}</dd>
                                </div>
                                <div class="app-fact">
                                    <dt>{{ t()('linked.lastSynced') }}</dt>
                                    <dd [title]="formatExact(d.syncedUtc)">{{ formatSyncedUtc() }}</dd>
                                </div>
                            </dl>
                        </app-section-card>
                    </div>
                </div>
            </p-tabpanel>

            <!-- ==================== OUTLETS (power distribution) ==================== -->
            @if (hasOutlets()) {
            <p-tabpanel value="outlets">
                @if (activeTab() === 'outlets') {
                <app-linked-device-power [linkedDeviceId]="device().id" />
                }
            </p-tabpanel>
            }

            <!-- ==================== COMMANDS ==================== -->
            @if (hasCommands()) {
            <p-tabpanel value="commands">
                @if (tunnelConnected() === false) {
                <app-empty-state icon="pi pi-cloud" [title]="t()('linked.tunnelTitle')" [description]="t()('linked.tunnel.commands')" />
                } @else {
                <div class="ld-stack">
                    <app-section-card icon="pi pi-code" [title]="t()('linked.tab.commands')" [count]="d.commands.length">
                        <p-select
                            [ngModel]="selectedCommand()"
                            (ngModelChange)="onCommandSelected($event)"
                            [options]="commandOptions()"
                            optionLabel="label"
                            optionValue="value"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            [group]="true"
                            [placeholder]="t()('linked.commands.choose')"
                            [filter]="(d.commands?.length ?? 0) > 5"
                            [filterPlaceholder]="t()('linked.commands.search')"
                            class="w-full"
                            appendTo="body"
                        >
                            <ng-template let-group #group>
                                <div class="flex items-center gap-2">
                                    <i class="pi pi-folder" aria-hidden="true"></i>
                                    <span class="font-semibold">{{ group.label }}</span>
                                </div>
                            </ng-template>
                            <ng-template let-item #item>
                                <div class="flex flex-col">
                                    <span>{{ item.label }}</span>
                                    <span class="text-xs text-color-secondary">{{ item.description }}</span>
                                </div>
                            </ng-template>
                        </p-select>
                    </app-section-card>

                    @if (selectedCommandDetails(); as cmd) {
                    <app-section-card icon="pi pi-play-circle" [title]="cmd.name" [subtitle]="cmd.description">
                        @if (cmd.category) {
                        <p-tag badge [value]="cmd.category" severity="secondary" />
                        } @if (cmd.arguments?.length > 0) {
                        <div class="ld-args">
                            <div class="ld-args__heading">{{ t()('linked.commands.parameters') }}</div>
                            @for (arg of cmd.arguments; track arg.name) {
                            <div class="ld-arg">
                                <div class="flex items-center gap-2">
                                    <label class="font-medium" [for]="'arg-' + arg.name">{{ arg.label }}</label>
                                    <span class="text-xs" [class.text-status-offline]="arg.required" [class.text-color-secondary]="!arg.required">{{ t()(arg.required ? 'linked.commands.required' : 'linked.commands.optional') }}</span>
                                </div>
                                @if (arg.description) {
                                <div class="text-xs text-color-secondary">{{ arg.description }}</div>
                                } @if (arg.type === 'String') {
                                <input pInputText [id]="'arg-' + arg.name" [placeholder]="arg.label" [(ngModel)]="commandArgs[arg.name]" class="w-full" />
                                } @if (arg.type === 'Bool') {
                                <div class="flex items-center gap-2">
                                    <p-toggleswitch [inputId]="'arg-' + arg.name" [(ngModel)]="commandArgs[arg.name]"></p-toggleswitch>
                                    <span class="text-sm">{{ t()(commandArgs[arg.name] ? 'linked.commands.yes' : 'linked.commands.no') }}</span>
                                </div>
                                } @if (arg.type === 'Int') {
                                <p-inputnumber
                                    [inputId]="'arg-' + arg.name"
                                    [(ngModel)]="commandArgs[arg.name]"
                                    [min]="arg.min ?? undefined"
                                    [max]="arg.max ?? undefined"
                                    [showButtons]="true"
                                    mode="decimal"
                                    [useGrouping]="false"
                                    class="w-full"
                                ></p-inputnumber>
                                } @if (arg.type === 'Double') {
                                <p-inputnumber
                                    [inputId]="'arg-' + arg.name"
                                    [(ngModel)]="commandArgs[arg.name]"
                                    [min]="arg.min ?? undefined"
                                    [max]="arg.max ?? undefined"
                                    mode="decimal"
                                    [minFractionDigits]="1"
                                    [maxFractionDigits]="3"
                                    class="w-full"
                                ></p-inputnumber>
                                } @if (arg.type === 'Select') {
                                <p-select [inputId]="'arg-' + arg.name" [(ngModel)]="commandArgs[arg.name]" [options]="arg.options || []" [placeholder]="arg.label" class="w-full" appendTo="body"></p-select>
                                } @if ((arg.type === 'Int' || arg.type === 'Double') && (arg.min !== undefined || arg.max !== undefined)) {
                                <div class="text-xs text-color-secondary">
                                    @if (arg.min !== undefined) { <span>{{ t()('linked.commands.min') }}: {{ arg.min }}</span> } @if (arg.min !== undefined && arg.max !== undefined) { <span> · </span> } @if (arg.max !== undefined) {
                                    <span>{{ t()('linked.commands.max') }}: {{ arg.max }}</span> }
                                </div>
                                }
                            </div>
                            }
                        </div>
                        } @else {
                        <p class="ld-muted">{{ t()('linked.commands.noParameters') }}</p>
                        }
                        <div class="ld-run">
                            <p-button [label]="t()(commandExecuting() ? 'linked.commands.running' : 'linked.commands.run')" icon="pi pi-play" [loading]="commandExecuting()" [disabled]="commandExecuting()" (onClick)="executeCommand()" />
                        </div>
                    </app-section-card>
                    } @if (commandResult(); as result) {
                    <div class="command-result" [ngClass]="result.success ? 'success' : 'error'" role="status">
                        <div class="flex items-center gap-2 mb-2">
                            <i [class]="result.success ? 'pi pi-check-circle text-status-online' : 'pi pi-times-circle text-status-offline'" aria-hidden="true"></i>
                            <span class="font-medium">{{ t()(result.success ? 'linked.commands.succeeded' : 'linked.commands.failed') }}</span>
                        </div>
                        <div class="text-sm text-color-secondary">{{ result.message }}</div>
                        @if (result.data) {
                        <pre class="mt-2 text-xs overflow-auto result-data-pre p-2 rounded">{{ result.data | json }}</pre>
                        }
                    </div>
                    }
                </div>
                }
            </p-tabpanel>
            }

            <!-- ==================== DIAGNOSTICS ==================== -->
            @if (hasDiagnostics()) {
            <p-tabpanel value="diagnostics">
                @if (tunnelConnected() === false) {
                <app-empty-state icon="pi pi-cloud" [title]="t()('linked.tunnelTitle')" [description]="t()('linked.tunnel.diagnostics')" />
                } @else {
                <div class="ld-stack">
                    <app-section-card icon="pi pi-cog" [title]="t()('linked.debug.enable')" [subtitle]="t()(isPanel() ? 'linked.debug.panelEnableDetail' : 'linked.debug.enableDetail')">
                        <p-toggleswitch actions [ngModel]="debugEnabled()" (ngModelChange)="toggleDebug()" [disabled]="debugToggling()" [ariaLabel]="t()('linked.debug.enable')"></p-toggleswitch>
                        @if (debugEnabled() && debugTimeRemainingMinutes() !== null) {
                        <div class="debug-countdown">
                            <div class="flex items-center justify-between mb-2">
                                <span class="text-sm font-medium">{{ t()('linked.debug.countdown') }}</span>
                                <span class="text-sm text-color-secondary">{{ debugTimeRemainingText() }}</span>
                            </div>
                            <p-progressbar [value]="debugTimeRemainingPercent()" [showValue]="false" class="h-2"></p-progressbar>
                        </div>
                        } @if (!debugEnabled()) {
                        <p class="ld-note"><i class="pi pi-info-circle" aria-hidden="true"></i><span>{{ t()('linked.debug.off') }}</span></p>
                        }
                    </app-section-card>

                    <app-section-card icon="pi pi-list" [title]="t()('linked.debug.logs')" [subtitle]="t()('linked.debug.entries').replace('{n}', '' + debugLogs().length)">
                        @if (debugEnabled()) {
                        <p-tag badge severity="success" [value]="t()('linked.debug.live')" icon="pi pi-circle-fill" />
                        }
                        <div actions class="flex items-center gap-1">
                            <p-button icon="pi pi-refresh" [text]="true" [rounded]="true" [loading]="debugLogsLoading()" [ariaLabel]="t()('linked.debug.refresh')" [pTooltip]="t()('linked.debug.refresh')" (onClick)="refreshDebugLogs()" />
                            @if (debugLogs().length > 0) {
                            <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger" [ariaLabel]="t()('linked.debug.clear')" [pTooltip]="t()('linked.debug.clear')" (onClick)="clearDebugLogs()" />
                            }
                        </div>
                        @if (!debugEnabled() && debugLogs().length === 0) {
                        <app-empty-state icon="pi pi-info-circle" [compact]="true" [title]="t()('linked.debug.empty')" />
                        } @else {
                        <p-scrollpanel [style]="{ width: '100%', height: '400px' }">
                            <div class="debug-log-viewer font-mono text-sm p-3" #logContainer>
                                @for (log of debugLogs(); track trackByTimestamp($index, log)) {
                                <div class="flex gap-2 py-1 log-entry" [ngClass]="{ 'text-status-offline': log.level === 'Error', 'text-yellow-400': log.level === 'Warning', 'text-blue-400': log.level === 'Info' }">
                                    <span class="log-timestamp shrink-0">{{ formatTime(log.timestampUtc) }}</span>
                                    <span class="shrink-0 w-16">[{{ log.level }}]</span>
                                    <span class="break-all">{{ log.message }}</span>
                                </div>
                                } @if (debugLogs().length === 0 && debugEnabled()) {
                                <div class="text-color-secondary text-center py-4">{{ t()('linked.debug.waiting') }}</div>
                                }
                            </div>
                        </p-scrollpanel>
                        }
                    </app-section-card>
                </div>
                }
            </p-tabpanel>
            }
        </p-tabpanels>
    </p-tabs>
    }
</div>

<!-- Move Dialog -->
<p-dialog [visible]="showMoveDialog()" (visibleChange)="showMoveDialog.set($event)" [header]="t()('linked.moveDialog.title')" [modal]="true" [closable]="false" [style]="{ width: '40rem', maxHeight: '80vh' }">
    @if (device(); as d) {
    <div class="mb-3">
        <div class="font-semibold">{{ displayName(d) }}</div>
        <div class="text-xs text-color-secondary font-mono">{{ d.uniqueId }}</div>
    </div>
    <app-location-picker [locations]="availableLocations()" (locationSelected)="selectedTargetLocation.set($event)"></app-location-picker>
    @if (moveError()) {
    <p-message severity="error" class="mt-3">{{ moveError()! }}</p-message>
    }
    <div class="flex justify-end gap-2 mt-4">
        <p-button [label]="t()('linked.moveDialog.cancel')" severity="secondary" [outlined]="true" [disabled]="moveSubmitting()" (onClick)="showMoveDialog.set(false)" />
        <p-button [label]="t()('linked.move')" [disabled]="!selectedTargetLocation() || moveSubmitting()" [loading]="moveSubmitting()" (onClick)="confirmMove()" />
    </div>
    }
</p-dialog>

<!-- Remove Dialog -->
<p-dialog [visible]="showRemoveDialog()" (visibleChange)="showRemoveDialog.set($event)" [header]="t()('linked.removeDialog.title')" [modal]="true" [closable]="false" [style]="{ width: '440px' }">
    @if (device(); as d) {
    <p>
        <strong>{{ displayName(d) }}</strong>
    </p>
    <p>{{ t()('linked.removeDialog.question') }}</p>
    <p class="text-color-secondary text-sm">{{ t()('linked.removeDialog.detail') }}</p>
    @if (removeError()) {
    <p-message severity="error" class="mt-3">{{ removeError()! }}</p-message>
    }
    <div class="flex justify-end gap-2 mt-4">
        <p-button [label]="t()('linked.moveDialog.cancel')" severity="secondary" [outlined]="true" [disabled]="removeSubmitting()" (onClick)="showRemoveDialog.set(false)" />
        <p-button [label]="t()('linked.remove')" icon="pi pi-trash" severity="danger" [disabled]="removeSubmitting()" [loading]="removeSubmitting()" (onClick)="confirmRemove()" />
    </div>
    }
</p-dialog>
